/**
 * useAIPresentationEdit Hook
 *
 * React hook for AI-driven presentation layer editing.
 * Provides methods to apply AI-generated commands directly to MMOs.
 * Uses IntentClassifier for smart routing and magnitude-aware deltas.
 */

import { useContext, useCallback, useMemo, useRef } from 'react';
import { MachineContext } from '../../fsm/useMachine';
import {
  MMOCommand,
  CommandResult,
  ExecutionContext,
  CommandExecutor,
  createCommandExecutor,
  buildScreenContext,
  ScreenContext,
} from '../commands';
import {
  classifyIntent,
  getMagnitudeDeltas,
  analyzeScreen,
  formatContextForPrompt,
  EditHistory,
  validateCommands,
  resolveTargets,
  ClassifiedIntent,
  RichScreenContext,
  ValidationResult,
} from '../intelligence';
import { FlowWindow } from '../../utils/globals';

declare let window: FlowWindow;

/**
 * Edit mode for AI operations
 */
export type AIEditMode = 'content' | 'presentation';

/**
 * Result of edit mode detection
 */
export interface EditModeResult {
  mode: AIEditMode;
  confidence: number;
  reason: string;
}

/**
 * Patterns for detecting presentation layer edits
 */
const PRESENTATION_PATTERNS = [
  // Size changes
  /\b(make|set).*(bigger|larger|smaller|tiny|huge)/i,
  /\b(increase|decrease|grow|shrink).*(size|font|text)/i,
  /\b(resize|scale)\b/i,

  // Position changes
  /\b(move|shift|position).*(left|right|up|down|top|bottom|center)/i,
  /\b(align|center|left-align|right-align)\b/i,
  /\bput.*(left|right|top|bottom)/i,

  // Color changes
  /\b(change|set|make).*(color|colour).*(to|blue|red|green|white|black|dark|light)/i,
  /\b(make|set|change).*\b(blue|red|green|yellow|orange|purple|pink|white|black|gray|grey)\b/i,
  /\bbackground.*(to|color|dark|light)/i,

  // Font changes
  /\b(use|change|set).*(font|typeface)/i,
  /\bmake.*(bold|italic|larger|smaller)/i,
  /\b(bolder|lighter)\b/i,

  // Layer operations
  /\b(bring|send).*(front|back|forward|backward)/i,
  /\b(on top|behind)\b/i,

  // Element operations (direct manipulation)
  /\b(rotate|flip)\b/i,
  /\b(border|outline|radius|rounded)\b/i,
  /\b(delete|remove).*(shape|image|text|element|box|rectangle)/i,
  /\b(add|create|insert).*(shape|rectangle|circle|box)/i,

  // Theme changes
  /\b(?:change|switch|set|use|apply)\s+(?:the\s+)?theme\b/i,
  /\b(?:make\s+it|switch\s+to)\s+(?:more\s+)?(?:dark|light|minimal|professional|corporate)\b/i,

  // Undo
  /\bundo\b/i,
  /\brevert\b/i,
];

/**
 * Patterns for detecting content layer edits
 * NOTE: These trigger FULL RECOMPILE via Claude API - expensive and slow!
 * Only use for structural changes that require regenerating the whole flow.
 * Text edits like "expand" should stay in presentation mode.
 */
const CONTENT_PATTERNS = [
  // Structural changes (require recompile)
  /\b(add|create|insert).*(slide|screen|section)/i,
  /\b(delete|remove).*(slide|screen)/i,
  /\b(reorganize|restructure|rearrange).*(content|slides)/i,

  // Multi-slide content generation
  /\b(generate|create).*(presentation|slides|deck)/i,
];

/**
 * Detect whether a message should trigger content or presentation layer editing
 */
export function detectEditMode(message: string): EditModeResult {
  const normalizedMessage = message.toLowerCase().trim();

  // Count pattern matches
  let presentationScore = 0;
  let contentScore = 0;

  for (const pattern of PRESENTATION_PATTERNS) {
    if (pattern.test(normalizedMessage)) {
      presentationScore++;
    }
  }

  for (const pattern of CONTENT_PATTERNS) {
    if (pattern.test(normalizedMessage)) {
      contentScore++;
    }
  }

  // Determine mode based on scores
  if (presentationScore > contentScore) {
    return {
      mode: 'presentation',
      confidence: Math.min(0.95, 0.5 + presentationScore * 0.15),
      reason: `Matched ${presentationScore} presentation patterns`,
    };
  } else if (contentScore > presentationScore) {
    return {
      mode: 'content',
      confidence: Math.min(0.95, 0.5 + contentScore * 0.15),
      reason: `Matched ${contentScore} content patterns`,
    };
  }

  // Default to presentation for short, action-oriented messages
  if (normalizedMessage.length < 50) {
    return {
      mode: 'presentation',
      confidence: 0.6,
      reason: 'Short message, defaulting to presentation edit',
    };
  }

  // Default to content for longer, descriptive messages
  return {
    mode: 'content',
    confidence: 0.6,
    reason: 'Longer message, defaulting to content edit',
  };
}

/**
 * Color map for local parsing
 */
const colors: Record<string, { r: number; g: number; b: number; a: number }> = {
  // Compound names first (checked before simple names)
  'dark blue': { r: 30, g: 41, b: 59, a: 1 },
  'dark green': { r: 22, g: 101, b: 52, a: 1 },
  'dark red': { r: 153, g: 27, b: 27, a: 1 },
  'dark gray': { r: 55, g: 65, b: 81, a: 1 },
  'dark grey': { r: 55, g: 65, b: 81, a: 1 },
  'light blue': { r: 147, g: 197, b: 253, a: 1 },
  'light green': { r: 134, g: 239, b: 172, a: 1 },
  'light gray': { r: 209, g: 213, b: 219, a: 1 },
  'light grey': { r: 209, g: 213, b: 219, a: 1 },
  // Simple names
  blue: { r: 59, g: 130, b: 246, a: 1 },
  red: { r: 239, g: 68, b: 68, a: 1 },
  green: { r: 34, g: 197, b: 94, a: 1 },
  yellow: { r: 250, g: 204, b: 21, a: 1 },
  orange: { r: 249, g: 115, b: 22, a: 1 },
  purple: { r: 168, g: 85, b: 247, a: 1 },
  pink: { r: 236, g: 72, b: 153, a: 1 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
  gray: { r: 107, g: 114, b: 128, a: 1 },
  grey: { r: 107, g: 114, b: 128, a: 1 },
  navy: { r: 30, g: 41, b: 59, a: 1 },
  teal: { r: 20, g: 184, b: 166, a: 1 },
};

/**
 * Parse common commands locally without LLM.
 * Uses IntentClassifier for magnitude-aware deltas and semantic role targeting.
 * Returns null if command cannot be parsed client-side.
 */
export function parseCommandLocally(
  message: string,
  context: ScreenContext,
  richContext?: RichScreenContext
): MMOCommand | MMOCommand[] | null {
  const intent = classifyIntent(message, richContext);
  console.log('[AI parseLocally] Intent:', intent.complexity, 'routeTo:', intent.routeTo, 'undo:', intent.isUndoRequest, 'design:', intent.isDesignIntent);

  // Undo requests are handled separately
  if (intent.isUndoRequest) return null;

  // Design intents need LLM
  if (intent.isDesignIntent) return null;

  // If routed to Ollama or Claude, don't parse locally
  if (intent.routeTo !== 'local') return null;

  const subIntents = intent.subIntents;
  if (subIntents.length === 0) return null;

  const commands: MMOCommand[] = [];

  for (const sub of subIntents) {
    const cmd = parseSubIntent(sub, context, richContext);
    if (cmd) {
      commands.push(cmd);
    } else {
      // If any sub-intent can't be parsed locally, bail to LLM
      return null;
    }
  }

  return commands.length === 1 ? commands[0] : commands.length > 0 ? commands : null;
}

/**
 * Parse a single sub-intent into a command
 */
function parseSubIntent(
  sub: { action: string; targetHint: string; magnitude?: string; value?: any; rawText?: string },
  context: ScreenContext,
  richContext?: RichScreenContext
): MMOCommand | null {
  const magnitude = (sub.magnitude || 'medium') as 'small' | 'medium' | 'large';
  const deltas = getMagnitudeDeltas(magnitude);

  // Resolve target using semantic roles if rich context available
  let targetDesc = sub.targetHint;
  let mmoIndex: number | undefined;

  if (richContext && sub.targetHint) {
    const resolved = resolveTargets(sub.targetHint, richContext);
    if (resolved.elements.length === 1) {
      mmoIndex = resolved.elements[0].index;
    }
  }

  const target = mmoIndex !== undefined
    ? { type: 'mmo' as const, mmoIndex }
    : { type: 'mmo' as const, description: targetDesc };

  const msg = sub.targetHint.toLowerCase();

  switch (sub.action) {
    case 'resize': {
      // Check for explicit absolute value (e.g., "to 48pt")
      if (sub.value && typeof sub.value === 'number') {
        return {
          type: 'mmo_edit',
          target,
          operations: [{ property: 'font.fontSize', value: sub.value }],
        };
      }
      if (sub.value?.multiplier) {
        // "double" or "half"
        const el = richContext?.elements.find((e) => e.index === mmoIndex);
        if (el?.fontSize) {
          const newSize = Math.round(el.fontSize * sub.value.multiplier);
          return {
            type: 'mmo_edit',
            target,
            operations: [{ property: 'font.fontSize', value: newSize }],
          };
        }
      }

      // Determine direction: "bigger" = positive delta, "smaller" = negative
      const direction = sub.value?.direction === 'down' ? -1 : 1;
      const delta = deltas.fontSize * direction;
      return {
        type: 'mmo_edit',
        target,
        operations: [{ property: 'font.fontSize', value: delta, relative: true }],
      };
    }

    case 'move': {
      const delta = deltas.position;
      // Use rawText for direction detection (contains full phrase like "move title left")
      const dirText = (sub.rawText || msg).toLowerCase();
      if (dirText.includes('left')) {
        return { type: 'mmo_edit', target, operations: [{ property: 'frame.x', value: -delta, relative: true }] };
      }
      if (dirText.includes('right')) {
        return { type: 'mmo_edit', target, operations: [{ property: 'frame.x', value: delta, relative: true }] };
      }
      if (dirText.includes('up')) {
        return { type: 'mmo_edit', target, operations: [{ property: 'frame.y', value: -delta, relative: true }] };
      }
      if (dirText.includes('down')) {
        return { type: 'mmo_edit', target, operations: [{ property: 'frame.y', value: delta, relative: true }] };
      }
      return null; // Can't determine direction
    }

    case 'recolor': {
      const colorText = (sub.rawText || msg).toLowerCase();
      // Background color
      if (colorText.includes('background') || targetDesc === 'background') {
        for (const [colorName, colorValue] of Object.entries(colors)) {
          if (sub.value === colorName || colorText.includes(colorName)) {
            return {
              type: 'screen_edit',
              target: { type: 'screen', screenIndex: context.screenIndex },
              operations: [{ property: 'background', value: colorValue }],
            };
          }
        }
      }
      // Text/element color
      for (const [colorName, colorValue] of Object.entries(colors)) {
        if (sub.value === colorName || colorText.includes(colorName)) {
          return {
            type: 'mmo_edit',
            target,
            operations: [{ property: 'textColor', value: colorValue }],
          };
        }
      }
      return null;
    }

    case 'align': {
      const alignText = (sub.rawText || msg).toLowerCase();
      let alignment = 'center';
      if (alignText.includes('left')) alignment = 'left';
      else if (alignText.includes('right')) alignment = 'right';
      return {
        type: 'mmo_edit',
        target,
        operations: [{ property: 'textAlignment', value: alignment }],
      };
    }

    case 'refont': {
      const fontText = (sub.rawText || msg).toLowerCase();
      if (fontText.includes('bold')) {
        return {
          type: 'mmo_edit',
          target,
          operations: [{ property: 'font.fontName', value: 'bold' }],
        };
      }
      return null;
    }

    case 'delete': {
      return {
        type: 'delete',
        target,
        operations: [],
      } as any;
    }

    case 'reorder': {
      const orderText = (sub.rawText || msg).toLowerCase();
      let direction: 'front' | 'back' | 'forward' | 'backward' = 'forward';
      if (orderText.includes('front')) direction = 'front';
      else if (orderText.includes('back')) direction = 'back';
      else if (orderText.includes('backward')) direction = 'backward';
      return {
        type: 'reorder',
        target,
        direction,
      } as any;
    }

    default:
      return null;
  }
}

/**
 * Hook return type
 */
export interface UseAIPresentationEditResult {
  /** Apply a single command */
  applyCommand: (command: MMOCommand) => CommandResult;

  /** Try to parse command locally without LLM */
  parseLocally: (message: string) => MMOCommand | MMOCommand[] | null;

  /** Apply multiple commands in sequence */
  applyCommands: (commands: MMOCommand[]) => CommandResult[];

  /** Get the current screen context for AI */
  getScreenContext: () => ScreenContext;

  /** Get rich screen context with semantic analysis */
  getRichScreenContext: () => RichScreenContext;

  /** Detect edit mode for a message */
  detectEditMode: (message: string) => EditModeResult;

  /** Classify intent with smart routing */
  classifyIntent: (message: string) => ClassifiedIntent;

  /** Validate commands before execution */
  validateCommands: (commands: MMOCommand[]) => ValidationResult;

  /** Undo the last AI edit */
  undo: () => { success: boolean; message: string };

  /** Check if undo is available */
  canUndo: boolean;

  /** Get edit history summary for LLM context */
  getEditHistorySummary: () => string;

  /** Record an edit in history */
  recordEdit: (userMessage: string, commands: MMOCommand[]) => void;

  /** Get formatted rich context for LLM prompts */
  getFormattedContext: () => string;

  /** Check if presentation editing is available */
  isAvailable: boolean;

  /** Current screen index */
  currentScreenIndex: number;
}

/**
 * Hook for AI presentation layer editing
 */
export function useAIPresentationEdit(): UseAIPresentationEditResult {
  const [current, send] = useContext(MachineContext);

  // Create executor with send function
  const executor = useMemo(() => createCommandExecutor(send), [send]);

  // Persistent edit history (survives re-renders)
  const editHistoryRef = useRef(new EditHistory());
  const editHistory = editHistoryRef.current;

  // Get content mapping from flow
  const contentMapping = useMemo(() => {
    if (window.flow_json?.flowboard?.contentMapping) {
      return window.flow_json.flowboard.contentMapping;
    }
    return undefined;
  }, []);

  // Current screen index
  const currentScreenIndex = current.context.index;

  // Build execution context
  const buildExecutionContext = useCallback((): ExecutionContext => {
    return {
      machineContext: current.context,
      screenIndex: currentScreenIndex,
      contentMapping,
    };
  }, [current.context, currentScreenIndex, contentMapping]);

  // Apply a single command
  const applyCommand = useCallback(
    (command: MMOCommand): CommandResult => {
      const context = buildExecutionContext();
      return executor.execute(command, context);
    },
    [executor, buildExecutionContext]
  );

  // Apply multiple commands
  const applyCommands = useCallback(
    (commands: MMOCommand[]): CommandResult[] => {
      const context = buildExecutionContext();
      return executor.executeBatch(commands, context);
    },
    [executor, buildExecutionContext]
  );

  // Get canvas size from flow
  const canvasSize = useMemo(() => {
    return current.context.flow.viewSize || { width: 1024, height: 748 };
  }, [current.context.flow.viewSize]);

  // Get screen context for AI
  const getScreenContext = useCallback((): ScreenContext => {
    const screen = current.context.flow.screens[currentScreenIndex];
    if (!screen) {
      return {
        screenIndex: currentScreenIndex,
        screenSize: canvasSize,
        background: { r: 255, g: 255, b: 255, a: 1 },
        elements: [],
      };
    }
    return buildScreenContext(screen, currentScreenIndex, contentMapping, canvasSize);
  }, [current.context.flow.screens, currentScreenIndex, contentMapping, canvasSize]);

  // Get rich screen context
  const getRichScreenContext = useCallback((): RichScreenContext => {
    const screen = current.context.flow.screens[currentScreenIndex];
    if (!screen) {
      return {
        screenIndex: currentScreenIndex,
        screenSize: canvasSize,
        background: { r: 255, g: 255, b: 255, a: 1 },
        backgroundLuminance: 1,
        elements: [],
        layoutPattern: 'scattered',
        designMetrics: { alignmentScore: 1, spacingConsistency: 1, contrastScore: 1, overallScore: 1 },
      };
    }
    return analyzeScreen(screen, currentScreenIndex, canvasSize);
  }, [current.context.flow.screens, currentScreenIndex, canvasSize]);

  // Get formatted context for LLM prompts
  const getFormattedContext = useCallback((): string => {
    const richCtx = getRichScreenContext();
    return formatContextForPrompt(richCtx);
  }, [getRichScreenContext]);

  // Check availability
  const isAvailable = current.context.flow.screens.length > 0;

  // Parse command locally without LLM
  const parseLocally = useCallback(
    (message: string): MMOCommand | MMOCommand[] | null => {
      const ctx = getScreenContext();
      const richCtx = getRichScreenContext();
      return parseCommandLocally(message, ctx, richCtx);
    },
    [getScreenContext, getRichScreenContext]
  );

  // Classify intent
  const classifyIntentFn = useCallback(
    (message: string): ClassifiedIntent => {
      const richCtx = getRichScreenContext();
      return classifyIntent(message, richCtx);
    },
    [getRichScreenContext]
  );

  // Validate commands
  const validateCommandsFn = useCallback(
    (commands: MMOCommand[]): ValidationResult => {
      const richCtx = getRichScreenContext();
      return validateCommands(commands, richCtx);
    },
    [getRichScreenContext]
  );

  // Undo
  const undo = useCallback((): { success: boolean; message: string } => {
    return editHistory.undo(send);
  }, [editHistory, send]);

  const canUndo = editHistory.canUndo();

  // Record edit
  const recordEdit = useCallback(
    (userMessage: string, commands: MMOCommand[]) => {
      const screen = current.context.flow.screens[currentScreenIndex];
      if (!screen) return;
      const snapshots = editHistory.captureSnapshot(commands, screen, currentScreenIndex);
      editHistory.record({
        userMessage,
        commands,
        screenIndex: currentScreenIndex,
        previousState: snapshots,
      });
    },
    [editHistory, current.context.flow.screens, currentScreenIndex]
  );

  // Get edit history summary
  const getEditHistorySummary = useCallback((): string => {
    return editHistory.getRecentSummary();
  }, [editHistory]);

  return {
    applyCommand,
    applyCommands,
    getScreenContext,
    getRichScreenContext,
    detectEditMode,
    classifyIntent: classifyIntentFn,
    validateCommands: validateCommandsFn,
    parseLocally,
    undo,
    canUndo,
    recordEdit,
    getEditHistorySummary,
    getFormattedContext,
    isAvailable,
    currentScreenIndex,
  };
}

export default useAIPresentationEdit;
